/**
 * Grafana Faro frontend RUM for the widget (F-3/F-4, MR-69; station-in-meta
 * per F-7/MR-72).
 *
 * Ported from postv2's `src/core/faro.js` with ONE deliberate deviation
 * (per the 2026-07-15 decision, amended 2026-07-16): postv2 does NOT filter
 * `NotAllowedError` (it can't tell an autoplay denial from a real permission
 * failure by message alone). This widget filters it from error reporting —
 * autoplay denial is the dominant benign case for an embedded audio player —
 * but does NOT drop it silently: it counts it as a Faro *measurement* so the
 * ramp isn't flying blind. See `NOT_ALLOWED_MEASUREMENT_TYPE` below for the
 * same ambiguity caveat postv2 documented (a rare clipboard/fullscreen denial
 * with the identical message text would also be counted here — acceptable
 * for a coarse ramp gauge, not acceptable for per-event attribution).
 *
 * ⚠️ Max-effort review finding (folded): a global `window.addEventListener(
 * 'unhandledrejection', ...)` inside `initFaro` is dead code for every
 * NotAllowedError call site that actually exists in this codebase today —
 * `Player.vue`'s `playMedia`/`playLiveStream` and `Menu.vue`'s clipboard copy
 * all already `.catch()` their own rejection (console-only, never
 * unhandled). So `reportNotAllowedIfApplicable` below is exported for THOSE
 * call sites to invoke directly from their own `.catch()` blocks (wired in
 * MR-70) instead of relying on a listener that never fires in practice.
 */
import {
  initializeFaro, getWebInstrumentations, InternalLoggerLevel, TransportItemType,
} from '@grafana/faro-web-sdk';
import { TracingInstrumentation, fetchCustomAttributeFunctionWithDefaults } from '@grafana/faro-web-tracing';
import { originFromCandidates } from '../utils/resize';
import { normalizeStation } from '../utils/station';
import { pushTelemetryEvent } from './telemetry';

// Stamp the embed's station onto browser→backend spans, so traces are
// filterable per station in Tempo (`{ span.app.station = "WXYZ" }`), mirroring
// postv2's stampStation. Unlike postv2 (which reads the current route), the
// widget's station is fixed for the embed's lifetime — it's the value passed
// into initFaro, read directly, no separate state to keep in sync.
const stampStation = (span, station) => {
  if (station) {
    span.setAttribute('app.station', station);
  }
};

// Every query parameter RETAINED THROUGH REDACTION, because it is ours rather than the
// host page's. Anything else in the iframe's query string did not come from us — see
// redactPageUrl.
//
// 🪤 D-232: THIS IS NOT "EVERY PARAM `src/` READS", AND THE DIFFERENCE MATTERS. That is
// how the list was originally derived and how this comment used to describe itself, but
// THREE of the entries have no `queryParams.*` reader in `src/` at all: `use_m`, `v`, and
// — since D-220 deleted its parse — `use_m4a`. MEASURED, with the probe validated against
// `play_button`, `start_remaining`, `v2_mods` and `full`, all of which do return real
// read sites.
//
// Retention is DELIBERATE and is not the same decision as widening. `use_m4a` is absent
// from `EMBED_ONLY_PARAMS` (`src/classes/widget.js`), so a host page's `?use_m4a=` still
// passes through into the iframe url, and the repo's own embed fixtures still carry it —
// so keeping the entry is what answers "does any embed still send this?". Dropping it
// would NARROW redaction and destroy that measurement.
//
// 🪤 DO NOT PRUNE THIS LIST BY GREPPING `src/` FOR READERS. That is the deletion this
// comment previously licensed. A param leaves only on evidence that no embed sends it any
// more, which is a different question from whether we still read it.
export const WIDGET_QUERY_PARAMS = [
  'accent', 'albumart', 'apiDomain', 'autoplay', 'background', 'colors', 'full',
  'futurishared', 'gallery', 'hashtags', 'id', 'limit', 'mode', 'mods', 'next', 'page',
  'perPage', 'play_button', 'shownotes', 'smc', 'src', 'start_remaining', 'station',
  'text', 'theme', 'use_m', 'use_m4a', 'v', 'v2_mods', 'version', 'vertical', 'zone',
];

// 🪤 D-211: `full` was MISSING from the list above until the widget started reading
// it, so `redactPageUrl` stripped the "Full vs Single" toggle out of `meta.page.url`
// — the bug hid itself from the beacon that would have found it. The fleet was then
// counted off `faro.performance.navigation`'s `event_data_name`, which at that time
// carried the unredacted iframe URL because `beforeSend` touched only `meta.page.url`.
//
// 🔴 THAT ESCAPE HATCH IS CLOSED AS OF D-214 — the navigation name now goes through
// this same allowlist (see `URL_BEARING_EVENT_ATTRIBUTES` below), so a param missing
// from the list is invisible in BOTH fields and there is no unredacted URL left to
// count around. The answer is to add the param here, not to find another field.
//
// So the drift the previous revision of this note recorded and deferred is fixed in
// the same commit that closes the hatch: `play_button`, `start_remaining`, `use_m4a`
// and `v2_mods` were the four the widget genuinely read, and they are now listed.
// 🪤 `use_m4a` IS NO LONGER READ — D-220 deleted its parse on 2026-09-06 — and it is
// retained anyway, deliberately: retention is the only way to answer "does any embed
// still send it?", which is exactly the question that deletion raises. See the leg in
// `faro-redaction-allowlist.spec.js` that pins it apart from the other three.
// 🪤 TWO NAMES IN THAT OLD NOTE WERE WRONG and are not added: `control_buttons` and
// `scrubber_handle` are read by NOTHING — MEASURED 2026-09-05, `grep -rn` over `src/`
// returns only this comment — so they were never ours to retain. Still drifted, and
// left so deliberately: `use_m` matches no real key (the key is `use_m4a`, now listed
// beside it) and `v` is read nowhere in `src/`. Both are inert entries; deleting them
// would NARROW retention, which is a separate change from widening it.

// 🔑 REDACT THE HOST PAGE'S QUERY STRING OUT OF `meta.page.url`.
//
// !263 declined to stamp the host href as an attribute because "Faro would be a NEW
// place it is RETAINED and queryable". Independent review measured that the full
// href was ALREADY in Faro, on EVERY payload, and had been all along: the SDK's
// `createPageMeta` is registered unconditionally and sets `page.url` to the IFRAME's
// location — and `classes/widget.js` builds that location by (a) copying the host
// page's ENTIRE query string into it verbatim and (b) appending
// `src=<encodeURIComponent(document.location.href)>`. So the host's session tokens
// and addresses were being retained twice per payload, in the one field the privacy
// analysis never looked at, while the analysis concluded the risk was avoided.
//
// ⚠️ The obvious remedy — overriding `page` in the Faro config — does NOT work:
// `createPageMeta` is added to `createDefaultMetas` unconditionally and wins. The
// working remedy is `beforeSend`, applied to the item on its way out.
//
// What survives: scheme, host, path, and the widget's OWN parameters, with `src`
// reduced to the host page's ORIGIN AND PATH (see hostPageIdentity — it used to be
// origin-only, which cost per-page attribution). What goes: every parameter we did
// not put there, because it came off the customer's page and is not ours to keep,
// plus the host page's own query string and fragment.

// WHICH PAGE the embed is on: the host page's origin and path, with the query string
// and fragment removed.
//
// 🔑 THE PATH IS KEPT DELIBERATELY (Adrian, 2026-08-20). This used to reduce `src` to a
// bare ORIGIN, which threw away the one dimension a per-embed question needs. It is what
// blocked closing the LCP finding of 2026-08-20: ruralradio.com is 61% of all widget RUM
// and 596 of the 706 samples over 10s, with p75 element_render_delay of 28,347ms against
// 346ms everywhere else — and no telemetry could say which of their pages the slow embeds
// sat on, so "the iframe is off-screen or in a collapsed container on some page" stayed a
// hypothesis instead of a finding.
//
// 🔴 THE QUERY AND HASH STILL GO, because that is where the disclosure actually lived.
// `classes/widget.js` copies the host page's ENTIRE query string into the iframe URL and
// appends `src=<encodeURIComponent(document.location.href)>`, so host session tokens and
// addresses were retained twice per payload. This keeps the path and drops the query and
// the fragment, which carries tokens as readily as a query does.
//
// 🪤 AND IT IS A BOUNDED WIDENING, NOT A CLEAN SEPARATION. An earlier version of this note
// said "a path identifies a page; a query string identifies a session", and a future
// reviewer would lean on that sentence — so it has to be right, and it is not. Two
// measured reasons:
//
//   1. A PATH CAN CARRY A SECRET. Retained verbatim from real traffic:
//      `/account/reset/eyJhbGciOiJIUzI1NiJ9-SECRET` and
//      `/users/adrianowen%40futurimedia.com/profile`. Keeping paths retains those.
//   2. THE `src` VALUE ITSELF CAME OUT OF A QUERY STRING until the last-wins fix below.
//      `get('src')` is first-wins and the loader copies the host page's query in FIRST, so
//      a host page carrying its own `?src=` decided what got retained — a path lifted out
//      of exactly the query string this block promises to drop.
//
// So: this WIDENS what is retained, deliberately, because per-page attribution was the one
// dimension a per-embed question needs and no other field carries it. It does not make the
// retained value safe by category. Treat it as a trade that was made, not a line that was
// drawn.
//
// Validation is INHERITED from originFromCandidates rather than re-implemented: it is
// what already rejects a non-absolute value and, importantly, the string 'null' that
// `URL.origin` yields for an opaque (data:/blob:/about:) origin. Returning '' means the
// caller sets no `src` at all, so an unparseable value fails CLOSED instead of passing
// the raw host href through.
//
// 🪤 A bare origin's pathname is '/', so `origin + pathname` would emit
// `https://host.example/` where the old code emitted `https://host.example` — a SECOND
// label for one page in every group-by, the same double-counting the station
// normalization exists to prevent. Collapse it.
function hostPageIdentity(rawSrc) {
  const origin = originFromCandidates(rawSrc, '');
  if (!origin) return '';
  try {
    const path = new URL(rawSrc).pathname;
    return path === '/' ? origin : `${origin}${path}`;
  } catch (e) {
    // originFromCandidates already parsed it, so this is unreachable — but a throw here
    // would surface inside beforeSend, and telemetry must never surface to the embed.
    return origin;
  }
}

export function redactPageUrl(rawUrl) {
  try {
    const u = new URL(rawUrl);
    const kept = new URLSearchParams();
    for (const name of WIDGET_QUERY_PARAMS) {
      if (!u.searchParams.has(name)) continue;
      // 🪤 `src` IS READ LAST-WINS, and `get()` — which is FIRST-wins — was the defect.
      // `formatUrlForIframe` (classes/widget.js) copies the host page's ENTIRE query string
      // into the iframe URL and only THEN appends its own `src=<encoded host href>`. So on
      // a host page that already carries a `src` parameter, `get('src')` returned the
      // CUSTOMER'S value, and this function retained an origin+path lifted out of the very
      // query string the block above promises to drop (measured: a crafted
      // `?src=https%3A%2F%2Fcdn.other.example%2Fsecret%2Fpath%3Ftok%3DSEKRIT` came back out
      // as `https://cdn.other.example/secret/path`). An ordinary UTM-style
      // `?src=newsletter` had the mirror-image effect: unparseable, so NO src was retained
      // and the page attribution was silently absent.
      //
      // Last-wins is correct BY CONSTRUCTION, not by luck — the loader's append is
      // unconditional and final, so the last value is always the one we put there.
      // Deliberately `src`-only: every other name here is ours by convention, and taking
      // the last of them would be a behaviour change nothing asked for.
      const value = name === 'src'
        ? (u.searchParams.getAll('src').pop() || '')
        : u.searchParams.get(name);
      if (name === 'src') {
        const hostPage = hostPageIdentity(value);
        if (hostPage) kept.set('src', hostPage);
      } else {
        kept.set(name, value);
      }
    }
    const query = kept.toString();
    return `${u.origin}${u.pathname}${query ? `?${query}` : ''}`;
  } catch (e) {
    // Not a parseable URL — returning it unchanged would be the failure mode this
    // exists to prevent, so return nothing rather than something unexamined.
    return '';
  }
}

// 🔴 D-214 — `meta.page.url` WAS NOT THE ONLY FIELD CARRYING THAT URL.
//
// The performance instrumentation's navigation entry has the iframe's own document URL as
// its `.name`, and the SDK copies it through verbatim: `createFaroResourceTiming` returns
// `name: name`, `createFaroNavigationTiming` spreads that in, and `getNavigationTimings`
// pushes the result as the ATTRIBUTES of a `faro.performance.navigation` event. Loki
// flattens event attributes to `event_data_*`, so the host page's whole query string and
// the appended `src=<encoded host href>` shipped in `event_data_name` on every navigation
// payload — while `meta.page.url`, holding the SAME STRING, sat next to it redacted.
//
// 🔑 So this is a coherence fix, not a second feature: one string reached the collector
// through two fields IN THE EVENT STREAM and only one was cleaned. It therefore reuses
// `redactPageUrl` unchanged rather than growing a parallel rule that could drift from it
// — the spec pins the two fields EQUAL to each other for exactly that reason.
//
// 🪤 "TWO FIELDS" IS THE EVENT STREAM ONLY, AND THE PREMISE IS NOT EXHAUSTIVE.
// `stacktrace.frames[].filename` is a THIRD carrier of the same string: the SDK's
// `buildStackFrame` falls back to `document.location.href` when a frame has no filename
// (`getDetailsFromErrorArgs` substitutes it whenever an error yields zero parsed frames).
// ✅ IT IS NOW HANDLED, by `redactStackFrameFilenames` below (D-221) — with exactly the
// equality gate against `meta.page.url` that this note said it would need, so genuine
// script urls survive untouched. The "two fields" premise above is kept as the record of
// what was once claimed, NOT as current state. The path was narrow either way: the widest
// shape, the cross-origin `"Script error."`, never becomes an item because `IGNORE_ERRORS`
// below drops it, and seven realistic V8/Firefox/WebKit stacks all keep a literal filename
// instead.
//
// 🪤 NAVIGATION ONLY, AND DELIBERATELY NOT `faro.performance.resource`. A resource entry's
// name is a SUBRESOURCE url — our own bundle, our own playlist API — so it carries no
// host-page data, and putting it through `redactPageUrl` would strip legitimate API query
// params and delete real telemetry to protect nothing. That is DERIVED, not asserted:
// `trackResources` defaults to xhr+fetch only, and every URL `src/utils/api.js` builds is
// made of `station`/`zone`/`futurishared`/`limit`/`page`/`perPage`/`id`/`category` — all
// either allowlisted above (so retained anyway) or unreachable from the host page. So no
// resource name can hold a value the navigation redaction would strip.
// "Redact every url-shaped attribute" is the tempting generalisation and it costs data for
// no privacy gain.
//
// 🪤 KEYED BY ATTRIBUTE, AND THE SHAPE IS THE POINT. The url-bearing attribute is not
// always called `name`. D-214 built this as a map rather than a list of event names
// precisely so the second event could be added without touching the walker, because a
// bare list would have invited the next reader to add the event name and get nothing.
//
// ⚖️ D-222, DECIDED 2026-09-06 (Adrian): ADD `securitypolicyviolation` NOW, pre-emptively,
// rather than gating it on a CSP ever being served. It cannot fire today — MEASURED
// 2026-09-05, neither the cluster widget host nor the CDN widget host (both in
// `config/hosts.js`) serves a `content-security-policy` header, and nothing in `src/` sets
// one — but ONE CSP header on the widget html flips it on, and the person adding a CSP
// header is not reading this file.
//
// 🔴 THE VALUE IS A REDACTOR PER ATTRIBUTE, NOT ONE RULE PER EVENT, BECAUSE THIS EVENT'S
// TWO URLS ARE NOT THE SAME KIND OF URL. That distinction is the whole of D-222's second
// half and the register left it open ("`referrer` may want dropping outright").
//
//   * `documentURI` is the IFRAME's own url — the same string `meta.page.url` holds — so
//     it takes `redactPageUrl` and D-214's coherence argument applies verbatim.
//   * `referrer` is, in an iframe, the HOST PAGE's full href. Putting THAT through
//     `redactPageUrl` is a category error: that allowlist means "parameters the WIDGET
//     reads", and on a customer's own url a name-collision is not a widget parameter.
//     MEASURED 2026-09-06 LOCAL —
//       in  https://embedder.example/newsroom?id=4242&page=3&next=/checkout&text=hello
//       out https://embedder.example/newsroom?id=4242&next=%2Fcheckout&page=3&text=hello
//     — four host-page params retained purely because their NAMES are also ours. So
//     `referrer` takes `hostPageIdentity`: origin+path, query and fragment dropped. That
//     is exactly the treatment `src` already gets inside `redactPageUrl`, and the one
//     Adrian settled for host-page attribution on 2026-08-20; dropping `referrer`
//     outright would instead throw away the per-page dimension that decision bought.
//
// ✅ `sourceFile` IS NOW HANDLED (D-226). It was reported and deliberately deferred when
// D-222 landed; this is the fix. The installed instrumentation
// (`instrumentations/csp/instrumentation.js`) forwards TWELVE fields, not the three named
// here — `blockedURI`, `columnNumber`, `disposition`, `documentURI`, `effectiveDirective`,
// `lineNumber`, `originalPolicy`, `referrer`, `sample`, `sourceFile`, `statusCode`,
// `violatedDirective`. Exactly ONE of the unhandled ones carries host-page data.
//
// 🔑 WHY `sourceFile` NEEDED A GATE RATHER THAN A MAP ENTRY. For an INLINE violation in
// the widget html it IS the iframe document url, `src=` param and all. MEASURED in real
// browsers: FIREFOX sets it to the full document url including the host href, while
// CHROMIUM strips the query. But in every non-inline case it is a GENUINE SCRIPT URL, and
// that url is the only thing saying where the violation came from — blanket redaction
// destroys the finding to protect it. So the rule is an EQUALITY GATE against
// `documentURI`, the same shape D-221 uses for stack frames and for the same reason.
//
// 🪤 `blockedURI` deliberately survives: it is the finding itself, and it names OUR
// blocked resource, not the host page. `sample` can hold 40 chars of the violating inline
// content, but the widget is always a CROSS-ORIGIN iframe, so that content is ours and
// never the customer's. `originalPolicy` is the widget document's own CSP. Checked, not
// assumed — see D-226's row for the per-field measurement.
// 🔑 D-228 — `referrer`'s THREE STATES ARE DISTINGUISHABLE, and they were not.
// `hostPageIdentity` returns '' for all of: a deliberately absent referrer, a value we
// could not parse, and a value that parsed but has no usable origin. Those arrived at the
// collector as one `event_data_referrer=""`, so A PARSE REGRESSION OF OURS WAS
// INDISTINGUISHABLE FROM CUSTOMERS TIGHTENING THEIR REFERRER POLICY — and neither was
// countable. `Referrer-Policy: no-referrer` really does produce '' (MEASURED in a real
// browser), so the collision is with a live, deliberate customer choice.
//
// 🪤 THE SENTINELS ARE FOR THIS EVENT ATTRIBUTE ONLY, NOT FOR `hostPageIdentity`. The same
// helper builds `meta.page.url`'s `src=` parameter, where '' means OMIT THE PARAMETER —
// emitting "(unparseable)" there would put a fake url into the page meta. The two call
// sites want opposite things from the same blank, which is the other half of D-228; they
// are kept apart deliberately and a leg pins that they stay apart.
//
// Parenthesised so they cannot be mistaken for urls, and so they are greppable in Loki.
const REFERRER_UNPARSEABLE = '(unparseable)';
const REFERRER_OPAQUE = '(opaque-origin)';

function referrerIdentity(rawReferrer) {
  // Absent is a CUSTOMER CHOICE and stays empty. It is information, not a failure, and
  // dressing it as one would destroy the very distinction this function exists to make.
  if (!rawReferrer) return '';
  const identity = hostPageIdentity(rawReferrer);
  if (identity) return identity;
  // Blanked. Separate "did not parse" from "parsed with no usable origin" — `about:blank`,
  // `data:`, `blob:` and the literal "null" are all real values a browser can send.
  if (rawReferrer === 'null') return REFERRER_OPAQUE;
  try {
    const origin = new URL(rawReferrer).origin;
    // A truthy, non-"null" origin would have been returned by hostPageIdentity above, so
    // reaching here after a successful parse means the origin is opaque.
    return !origin || origin === 'null' ? REFERRER_OPAQUE : REFERRER_UNPARSEABLE;
  } catch (e) {
    return REFERRER_UNPARSEABLE;
  }
}

const redactSourceFile = (value, attributes) => (
  // Only when it IS the document url. No `documentURI` to compare against means we cannot
  // prove that, so the value ships — a genuine script url is worth more than a speculative
  // rewrite. That residual is pinned by a leg in `faro.spec.js` so it stays a decision.
  attributes && attributes.documentURI === value ? redactPageUrl(value) : value
);

const URL_BEARING_EVENT_ATTRIBUTES = {
  'faro.performance.navigation': { name: redactPageUrl },
  securitypolicyviolation: {
    documentURI: redactPageUrl,
    referrer: referrerIdentity,
    sourceFile: redactSourceFile,
  },
};

function redactEventUrlAttribute(item) {
  // 🪤 `TransportItemType.EVENT`, NOT the literal `'event'`. This file already records
  // that "a hardcoded vendor name is a filter with a half-life — a rename upstream turns
  // it into a no-op that nothing can see", and pins its OTHER vendor strings against the
  // installed build. This one is held the same way: the enum comes from the SDK, and
  // `faro.spec.js`'s canary leg asserts the event name still exists in the installed
  // instrumentation. `package.json` floats `^2.7.0`, so the lockfile is the only thing
  // making the strings stable, and a lock refresh is not a code review.
  if (!item || item.type !== TransportItemType.EVENT) return item;
  const payload = item.payload;
  const urlAttributes = payload && URL_BEARING_EVENT_ATTRIBUTES[payload.name];
  if (!urlAttributes) return item;
  const attributes = payload.attributes;
  if (!attributes) return item;
  // Every url-bearing attribute the map names for this event, not just the first —
  // `securitypolicyviolation` carries two, and they take DIFFERENT redactors (see above).
  // 🪤 `Object.keys`, not `Object.entries`: `Object.entries` is ES2017 and appears nowhere
  // else in `src/`, while `vite.config.js` pins `target: 'es2015'` — which transforms
  // syntax and does not polyfill runtime methods. `Object.keys` is already used four lines
  // below, so it is known-safe on every browser this bundle reaches.
  const patch = {};
  for (const key of Object.keys(urlAttributes)) {
    if (!attributes[key]) continue;
    // D-229: the redactor is handed the SIBLING attributes as well as its own value.
    // `sourceFile`'s rule is cross-attribute — it cannot be written against the value
    // alone — and the map's previous `(value) => value` contract could not express it.
    // Backward-compatible: `redactPageUrl` and `hostPageIdentity` ignore the extra arg.
    const redacted = urlAttributes[key](attributes[key], attributes);
    if (redacted !== attributes[key]) patch[key] = redacted;
  }
  // Nothing to do — and returning the original object keeps this a no-op rather than an
  // allocation on every payload that was already clean.
  if (Object.keys(patch).length === 0) return item;
  // NB Object.assign, not object spread — same ES2017 parser constraint as beforeSend.
  const nextAttributes = Object.assign({}, attributes, patch);
  const nextPayload = Object.assign({}, payload, { attributes: nextAttributes });
  return Object.assign({}, item, { payload: nextPayload });
}

// D-221 — the THIRD carrier of the iframe url, and the one the "two fields" note above
// deliberately left open. The SDK's `buildStackFrame` substitutes `document.location.href`
// when a frame has no filename, and `getDetailsFromErrorArgs` does the same whenever an
// error yields zero parsed frames. That url carries the HOST page's whole query string
// (`classes/widget.js` copies it in), so an exception can disclose what `beforeSend`
// already strips from `meta.page.url`.
//
// 🔑 AN EQUALITY GATE, NOT A BLANKET REDACTION — this is the whole design, and the
// tempting generalisation is the bug. A frame filename is USUALLY a genuine script url;
// putting every filename through `redactPageUrl` would strip real bundle identity out of
// every stack trace to protect nothing. Only a filename that is EXACTLY the substituted
// page url is rewritten, and it is rewritten to the same redacted string the page meta
// gets, so the two cannot drift.
//
// 🪤 EXACT equality, not a prefix test. A prefix match would rewrite a real script url
// that happens to share the origin+path, which is most of ours.
//
// 🪤 NOT D-214's mechanism. That keys on an event NAME; an exception is not an event, so
// reusing it here would have been a filter that never matches. Different stream, different
// gate — see `URL_BEARING_EVENT_ATTRIBUTES` above.
function redactStackFrameFilenames(item, rawPageUrl, redactedPageUrl) {
  if (!item || item.type !== TransportItemType.EXCEPTION) return item;
  if (!rawPageUrl || redactedPageUrl === rawPageUrl) return item;
  const payload = item.payload;
  const stacktrace = payload && payload.stacktrace;
  const frames = stacktrace && stacktrace.frames;
  if (!Array.isArray(frames)) return item;
  let changed = false;
  const nextFrames = frames.map((frame) => {
    if (!frame || frame.filename !== rawPageUrl) return frame;
    changed = true;
    // NB Object.assign, not object spread — same ES2017 parser constraint as beforeSend.
    return Object.assign({}, frame, { filename: redactedPageUrl });
  });
  // Nothing matched — return the ORIGINAL object rather than an equivalent copy, so a
  // clean stack costs no allocation and stays reference-identical for anything downstream.
  if (!changed) return item;
  const nextStacktrace = Object.assign({}, stacktrace, { frames: nextFrames });
  const nextPayload = Object.assign({}, payload, { stacktrace: nextStacktrace });
  return Object.assign({}, item, { payload: nextPayload });
}

// Faro measurement `type` for the filtered-but-counted NotAllowedError case
// (F-3). A measurement, not an error/event — it never reaches the exception
// stream, only a counted gauge.
export const NOT_ALLOWED_MEASUREMENT_TYPE = 'not_allowed_error';

// Token list for the third-party-globals pattern (ported verbatim from
// postv2 — same third-party actors, gtag/GA, googletag/GAM, __cmp (IAB
// consent), AviviD, silktideCookieBannerManager).
const THIRD_PARTY_GLOBALS = 'gtag|googletag|__cmp|AviviD|silktideCookieBannerManager';

// F-3: widget-tuned IGNORE_ERRORS. Three patterns ported verbatim from
// postv2 (ResizeObserver/Script-error/play-interrupted/third-party-globals),
// PLUS the one deliberate addition vs postv2: autoplay NotAllowedError.
export const IGNORE_ERRORS = [
  // ResizeObserver couldn't drain all notifications in one animation frame;
  // the browser defers the rest (nothing lost). Covers the current Chromium
  // wording and the legacy one.
  /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/,
  // Cross-origin script threw and the browser stripped detail for CORS
  // reasons: window.onerror reports the literal "Script error." with no
  // stack/line. The (?=\s|$) lookahead pins the exact sentinel so a real
  // error merely STARTING with "Script error…" still surfaces.
  /^Script error\.?(?=\s|$)/,
  // HTMLMediaElement play/pause race — a new load()/pause() interrupts an
  // in-flight play() promise (AbortError). Expected during normal episode
  // switching. Firefox's differently-worded equivalent is NOT matched (it's
  // reused for real media-fetch failures there).
  /The play\(\) request was interrupted/,
  // Globals injected by third-party ad/analytics/consent scripts (or browser
  // extensions) on the HOST page the widget is embedded in — not our bundle.
  // Matched in both engine wordings: V8/Firefox "<g> is not defined",
  // WebKit/Safari "Can't find variable: <g>". Word-boundaried so a longer
  // identifier containing a token still surfaces.
  new RegExp(
    `\\b(?:${THIRD_PARTY_GLOBALS}) is not defined\\b` +
      `|Can't find variable: (?:${THIRD_PARTY_GLOBALS})\\b`
  ),
  // Autoplay denial (DOMException NotAllowedError) — the dominant benign
  // error for an embedded audio player under strict autoplay policies.
  // Deliberately broad (matches ANY NotAllowedError message, not just the
  // autoplay wording) because Faro's matched string for this DOMException is
  // `${name}: ${message}` and the message text is identical across autoplay/
  // clipboard/fullscreen denials (see postv2's faro.js — same ambiguity,
  // opposite conclusion). Counted via NOT_ALLOWED_MEASUREMENT_TYPE below so
  // the ramp isn't blind to it.
  /^NotAllowedError:/,
];

// Duck-typing alone (`reason.name === 'NotAllowedError'`) would let any plain
// object/custom Error with that name slip through as a real autoplay/
// permission denial. Real browsers only ever produce this name on a genuine
// DOMException, so require that too where the class exists at all (a
// non-browser/minimal test environment without a global DOMException skips
// the check rather than false-negative on every input).
function isNotAllowedRejection(reason) {
  if (!reason || reason.name !== 'NotAllowedError') return false;
  if (typeof DOMException !== 'undefined' && !(reason instanceof DOMException)) return false;
  return true;
}

// F-3: count (never report) a NotAllowedError-shaped rejection as a Faro
// measurement. Exported for callers OUTSIDE this module — the actual
// NotAllowedError call sites (Player.vue's play(), Menu.vue's clipboard
// copy) already `.catch()` their own rejection (see the module doc above),
// so counting has to happen AT those call sites, not via a global
// `unhandledrejection` listener here (that never fires for an already-caught
// rejection). Reads the global `window.faro` instance (mirroring
// `core/telemetry.js`'s established no-op-safe pattern) since a component
// like Player.vue has no direct reference to the instance `initFaro` returns
// — that's scoped to app.vue's own call. Never throws to the caller.
//
// `skipDedupe: true`: faro-web-sdk's `makeCoreConfig` defaults `dedupe: true`
// (verified against the installed SDK — NOT faro-core's raw default, which
// has none), and every call here pushes an identical `{type, values}` shape
// for a given station — without this, only the FIRST NotAllowedError per
// station would ever be counted; every repeat would be silently deduped.
export function reportNotAllowedIfApplicable(error, station) {
  try {
    const w = typeof window !== 'undefined' ? window : undefined;
    const faro = w && w.faro;
    if (faro && faro.api && typeof faro.api.pushMeasurement === 'function' && isNotAllowedRejection(error)) {
      faro.api.pushMeasurement(
        {
          type: NOT_ALLOWED_MEASUREMENT_TYPE,
          values: { count: 1 },
        },
        // context MUST ride the OPTIONS arg: faro-core reads `context` there and
        // overwrites payload.context with it, so a context passed in the payload
        // arg is silently discarded and per-station attribution is lost
        // (verified against the installed SDK's api/measurements/initialize.js).
        //
        // 🔴 normalizeStation, and this site is WORSE than the general case (review item
        // S11). Twelve call sites hand this the customer's embed-code spelling, and
        // `skipDedupe: true` exists precisely so per-station repeats are all counted — so a
        // station whose embeds are split across `KTICAM` and `kticam` had its autoplay-denial
        // rate reported as two smaller rates, and the obvious query ("which stations are
        // worst for blocked autoplay?") returned neither. Same defect as the widget_layout
        // stamp, on the one measurement whose whole point is counting repeats.
        {
          skipDedupe: true,
          context: normalizeStation(station) ? { station: normalizeStation(station) } : undefined,
        }
      );
    }
  } catch (e) {
    /* telemetry must never surface to the embed */
  }
}

// 🔑 THE RETRY EVENT, in ONE place for all three players.
//
// v2 (D-165), v3 and v4 all report the same thing: a listener met a dead player and
// asked for another go, and then whether that go worked. Three inline copies of an
// attribute set is where a schema drifts — one component gains a field, the dashboard
// stops comparing like with like, and nobody finds out because every copy still
// "works". This module already holds the other cross-version reporter
// (`reportNotAllowedIfApplicable`, which shipped v4-ONLY and was the ninth one-of-N
// defect in this codebase), so the precedent for where a shared reporter lives is set.
//
// Three phases, one event name, so the SUCCESS RATE is a single query rather than a
// join: `attempted` when the listener taps, then exactly one of `recovered` /
// `failed`. A count on its own cannot tell a fix that works from one that fires just
// as often and fails just as often.
//
// 🪤 NO URL RIDES THIS EVENT, and that is deliberate rather than an omission. D-164
// stripped the query string from the engine's own failure events because
// `hls_engine`'s suppression key already declares that per-resolve CDN tokens live
// there — and the event body was forwarding the raw url anyway, so the one field the
// module named as sensitive was the one field shipped to Faro. `source_kind`,
// `live` and `player_version` carry the attribution that matters and cannot carry a
// token. `station` is stamped onto every event by core/telemetry.
//
// Values are strings because Faro attributes are `Record<string, string>`; a boolean
// arrives at the collector coerced anyway, and coercing here keeps the two sides
// honest.
export function reportPlayerRetry(phase, { playerVersion, sourceKind, live }) {
  pushTelemetryEvent('widget_live_retry', {
    phase,
    player_version: playerVersion,
    source_kind: sourceKind,
    live: live ? 'true' : 'false',
  });
}

// F-4: derive the Faro collector URL + environment + version at RUNTIME from
// the resolved apiDomain — one built bundle (one STATIC_APP_VERSION hash) is
// shared across every cluster, so these can never be baked in at build time.
//
// environment: `<cluster>.futurimedia.com` -> "<cluster>", with the two prod
// aliases ("post" and "postv4") folded to "prod" — a DELIBERATE widget-only
// deviation: postv2 emits the raw `host.split('.')[0]` with no fold, so the
// widget reports environment="prod" while the in-app postv2 player reports
// "post"/"postv4" into the same prod cluster's Faro.
// version: passed through from buildHash — the widget's own `<branch>-<sha7>`
// identity, BAKED IN at build time (`__WIDGET_BUILD_VERSION__`, read by
// src/config/buildVersion.js). It used to be fetched at runtime from the now
// DEPRECATED WEBWIDGETV2-VERSION.txt marker, which 404'd on both prod hostnames
// and made this fall back to 'dev' for 107,231 of 107,233 measured RUM events;
// the fallback stays — it is what a dev-server or test build correctly reports —
// but it is no longer the production answer.
//
// A falsy apiDomain returns an EMPTY faroUrl (never a malformed
// `https:///-/faro/collect`) so the caller's own `if (faroUrl)` no-collector
// guard (F-5) actually engages instead of silently constructing garbage.
export function deriveFaroConfig(apiDomain, buildHash) {
  const host = apiDomain || '';
  if (!host) {
    return { faroUrl: '', environment: 'unknown', version: buildHash || 'dev' };
  }
  const label = host.split('.')[0] || 'unknown';
  const environment = label === 'post' || label === 'postv4' ? 'prod' : label;
  return {
    faroUrl: `https://${host}/-/faro/collect`,
    environment,
    version: buildHash || 'dev',
  };
}

// 🔴 SOME ENGINES CANNOT RUN THE PERFORMANCE INSTRUMENTATION, AND IT WAS REPORTING
// ITS OWN FAILURE AS OUR EXCEPTION.
//
// MEASURED on postv4 in the 90 minutes after the widget went live on
// `master-e7285ef`: 26 exceptions against 89,581 events. The widget-attributable ones were
// THREE error classes with one cause, and a fourth that is a DISJOINT COHORT this change
// cannot affect:
//
//   this change fixes    `TypeError: e.entries.at is not a function`          (10)
//                        `TypeError: this.i.at is not a function`             (6)
//                        `Member PerformanceObserverInit.entryTypes is …`     (1)
//   unrelated            `TypeError: Load failed`                            (5)
//
// 🔴 RE-MEASURED 2026-08-25 over a full 24h. Loki on the postv4 obs node, stream
// `{service_name="faro"}` (endpoint in the `obs-query` skill in post-reproducible-stuff, and
// deliberately not written out here — `hosts.spec.js` scans these comments for host literals).
// Same prod build `master-e7285ef`; 1,042,198 lines / 532 `kind=exception`:
//
//     `.at is not a function`                        337
//     `Member PerformanceObserverInit.entryTypes …`   76
//     `TypeError: Load failed`                        65 exceptions
//
// So the `entryTypes` class is ~3/hour, against the "one in ninety minutes" the first sample
// showed. Anything reasoning from the 90-minute figures — a priority call included — should use
// these.
//
// 🪤 AND THE FIRST VERSION OF THESE FIGURES WAS ~2x TOO HIGH, from the trap the obs-query
// skill documents in bold. They were taken with `query_range` + `step=86400` over a 24h range and
// then SUMMED — which evaluates `count_over_time([24h])` at several points and adds overlapping
// windows, so the total was closer to 72h of data wearing a 24h label. It read 2,128,317 lines /
// 844 exceptions / 456 `.at` / 171 `entryTypes`. Every count above is now a single INSTANT
// evaluation (`/loki/api/v1/query`, one window, one sample), cross-checked against a raw line
// pull that returned exactly 76. `count_over_time` sums are step-dependent; do not add them.
//
// 🪤 `TypeError: Load failed` IS NOT ONE OF THESE, and an earlier draft of this comment said
// "four error classes with ONE cause". It comes from modern engines that all have
// `Array.prototype.at`, so no feature detect suppresses it — and the verification recipe below
// is written accordingly. Expect the three to disappear and `Load failed` to remain; a reader
// who expects all four to go will read a working fix as a failed one.
//
// It is not our code and it is not a regression. 🪤 An earlier version of this line said
// "`.at(` appears nowhere in `src/`", asserted in the present tense — and it is self-refuting,
// because the grep it invites returns FOUR hits, every one of them inside these comments. (It
// said "three", and it was four at the commit that wrote the sentence — the fourth is the
// one-line note above `engineRunsWebVitals`. Same defect one turn later: a count asserted rather
// than run. RE-RUN 2026-08-25, `grep -rn '\.at(' src/` -> 4.)
// What is actually true, and what the claim was reaching for: no CODE in `src/` calls `.at(`,
// all five occurrences in the shipped bundle are vendor, and the count is IDENTICAL
// in the previous release's bundle. It is `web-vitals` — pulled transitively by
// `@grafana/faro-web-sdk` and activated by `getWebInstrumentations()` — running on an
// engine that predates `Array.prototype.at`. The bundle shows the receiver being an
// array LITERAL (`this.i = []`, then `this.i.at(-1)`), so the method really is the
// thing that is missing.
//
// 🪤 THE USER AGENTS LIE, WHICH IS WHY A UA CHECK WOULD NOT WORK HERE. The `.at` throwers
// report Chrome 109/Android 13 (12x) and Mobile Safari 16.3 (4x), and both of those versions
// shipped `.at` years earlier (Chrome 92, Safari 15.4). They are in-app WebViews advertising a
// modern version over an older engine. Only asking the engine what it can actually do gets the
// right answer.
//
// 🔑 TWO COHORTS, NOT ONE, AND CONFLATING THEM IS WHAT PRODUCED AN OVER-BROAD DROP:
//
//   * the `.at` failures are version-LYING WebViews (Chrome 109/Android 13, Mobile Safari
//     16.3) running an engine older than they claim. Capability: `Array.prototype.at`.
//   * the `PerformanceObserverInit.entryTypes` failure is honestly-versioned, genuinely OLD
//     Safari. Capability: `PerformanceObserver` accepting the single-`type:` form.
//
// They overlap but are not the same set, and each instrumentation is now gated on its OWN
// capability — see the two detects below.
//
// WHY FEATURE-DETECT AND NOT THE ALTERNATIVES:
//   - POLYFILLING `.at` fixes the two `.at` errors and not the
//     `PerformanceObserverInit` one, and it papers over an engine that cannot run
//     the instrumentation anyway — it would go on failing, differently.
//   - DROPPING web-vitals outright fixes it for everyone, at the cost of
//     CLS/INP/LCP/FCP for the ~99% of clients where it works fine.
//   - DETECTING keeps the metrics where they work, stays silent where they cannot,
//     and is cheap — RE-MEASURED 2026-08-25 after the detect became a PROBE: the main bundle
//     grows **881 B raw**, exactly, and roughly 284 B gzipped. (It was 833 / ~263 while the
//     performance detect was a property read; the try/catch/finally probe costs 48 B raw more,
//     and it is what stopped one `entryTypes` thrower in four.) Which matters, because "cheap"
//     is the only affordable answer this close to the 212,000 B ceiling in
//     `scripts/verify-artifact-shape.js`.
//
//     🪤 THIS COMMENT SAID "427 B raw and 136 B gzipped", AND BOTH NUMBERS WERE
//     STALE RATHER THAN WRONG-WHEN-WRITTEN. They were measured on 2026-08-24, before
//     the detect was split into its two real capabilities (`.at` for web-vitals,
//     `PerformanceObserverInit.entryTypes` for the performance instrumentation) —
//     the fix that stopped this file deleting working telemetry. The split roughly
//     doubled the cost, and the figure was never re-taken. Three separate
//     re-measurements in review disagreed with the 136 (137, 142, 143) and each was
//     also taken before the split, which is why none of them agreed with each other
//     either: they were measuring a bundle that no longer existed.
//
//     🔑 THE METHOD IS PART OF THE NUMBER, and naming it is what makes the stamp
//     checkable. Both figures are `zlib.gzipSync(level 9)` on dist/static's main
//     bundle — what Rule 5 of scripts/verify-artifact-shape.js compares — from two
//     full `npm run build`s of THIS tree that differ only in src/core/faro.js
//     (the other being origin/development's copy), with CI_COMMIT_BRANCH and
//     CI_COMMIT_SHA pinned so the baked build identity is byte-identical in both
//     and cannot move the delta.
//
//     🪤 AND THE OLD VERSION OF THIS PARAGRAPH PROMISED MORE THAN THE METHOD CAN
//     DELIVER. It ended "Re-run and it reproduces exactly", and quoted "270 B gzipped,
//     landing at 179,063 B = 84.46%". Neither survives re-running:
//       * the RAW delta does reproduce exactly — 881 B, because the identity string is
//         byte-identical across the pair and cancels.
//       * the GZIP delta does not. MEASURED 2026-08-25 by the recipe above: 284 B. gzip is
//         context-sensitive, so inserting the same bytes shifts the compressed total by a byte
//         or two depending on what surrounds them — the pre-probe figure measured 263 here and
//         264/265 in review on the merged tree, the same bytes three ways. Quote it as
//         "roughly 284 B", never to the byte.
//       * the ABSOLUTE cannot reproduce at all, because the recipe says to PIN
//         CI_COMMIT_BRANCH and CI_COMMIT_SHA without saying to WHAT — and the baked
//         identity is in the bundle. MEASURED, same tree, same commit, three pins:
//             CI_COMMIT_BRANCH=x                             -> 179,063 B
//             CI_COMMIT_BRANCH=size-probe                    -> 179,068 B
//             CI_COMMIT_BRANCH=a-very-long-branch-name-indeed -> 179,084 B
//         The old figure was the first of those. A number that moves with the branch NAME
//         is not a property of this change, so the absolute is dropped here: the gate in
//         `scripts/verify-artifact-shape.js` owns it, and it re-measures every build.
//
// This is telemetry degrading its own observability on a small set of mobile
// clients, not the widget breaking. Priority is low; being right about which
// instrumentations are dropped is not.
// 🪤 NAMED FOR THE SDK, NOT FOR THE PERFORMANCE INSTRUMENTATION. This was
// `PERFORMANCE_INSTRUMENTATION_PREFIX`, which reads as "the prefix of the performance
// instrumentation" — it is the prefix ALL of the SDK's instrumentations carry, errors and
// session and view included, and the code below strips it from whatever name it matched.
const WEB_SDK_INSTRUMENTATION_PREFIX = '@grafana/faro-web-sdk:instrumentation-';

// The two capability questions, each asked of the engine and not of its UA string.

// What `web-vitals` needs: it calls `.at(-1)` on an array literal.
function engineRunsWebVitals() {
  return typeof Array.prototype.at === 'function';
}

// What the SDK's performance instrumentation needs: `observe({ type: … })` rather than
// `observe({ entryTypes: [...] })`. That is the capability the
// `Member PerformanceObserverInit.entryTypes is required` error is about, so it is the capability
// this asks about — by ASKING, in a try/catch, rather than by proxy.
//
// 🔴🔴 THIS USED TO TEST FOR `supportedEntryTypes`, AND THAT MISSED ONE THROWER IN FOUR.
// The old form was `!!observer.supportedEntryTypes && typeof …length === 'number'`, justified by
// "it was added to the spec alongside the single-`type:` form, so an engine that demands
// `entryTypes` does not have it". They are NOT co-versioned. VERIFIED against WebKit's history for
// `Source/WebCore/page/PerformanceObserver.idl`:
//
//     a40220e  2019-01-25  Implement PerformanceObserver.supportedEntryTypes
//     d37ab8c  2020-07-28  PerformanceObserver should work with 'type' and not just `entryTypes`
//
// The second removes `required` from `entryTypes` and adds `DOMString type`. Eighteen months
// apart — the property is Safari 13, the `type:` form Safari 14. MDN browser-compat-data gives
// `supportedEntryTypes` as chrome 73 / safari 13; it carries no datum for the `type:` MEMBER, so
// the Safari 14 mapping rests on branch ancestry (`d37ab8c` is in `safari-610-branch`, not
// `safari-609-branch`) plus release dates, not on BCD. Blink split them too — BCD puts the `type:`
// form at Chrome 76 against `supportedEntryTypes` at 73 — so the premise was wrong in both
// engines; only the Safari cohort is still alive to show it.
//
// So Safari 13.x HAS the property and STILL requires `entryTypes`: it passed the old detect, kept
// the instrumentation, and threw. MEASURED in Faro RUM on postv4 over a 24h window to
// 2026-08-25 — the `entryTypes` exceptions, every one of them, by engine:
//
//     Safari / Mobile Safari 12.1.x    53   no `supportedEntryTypes` -> old detect caught it
//     Safari 13.1.2                    17   HAS it -> old detect did NOT
//     Chrome 92 on iOS 12.5 (CriOS)     6   iOS 12 WebKit underneath -> caught
//     ---------------------------------------
//                                      76   (a partition: the rows sum to the total)
//
// ⇒ better than one in five survived the old detect, and the same shape holds over 48h (31/138)
// and 72h (43/175). Safari 13.x reported ZERO `.at` errors in the same window, so nothing else
// was dropping it.
//
// 🔑 AND THE ARGUMENT AGAINST PROBING WAS WRONG, which is why the old form settled for a proxy.
// It read: "Checked without calling `observe`, because a failed probe is the very exception this
// exists to prevent." A CAUGHT throw is not an exception anyone sees — it reaches neither
// `window.onerror` nor `unhandledrejection`, so Faro's error instrumentation cannot observe it.
// `web-vitals` wraps its own `observe` in try/catch for exactly this reason.
//
// 🪤 `'mark'`, NOT `'navigation'` — even though `navigation` is what the SDK observes. An entry
// type the engine does not support does NOT throw; per spec `observe()` returns and quietly
// observes nothing. Probing `navigation` would therefore conflate "rejects the `type:` SHAPE"
// (what this asks) with "does not know that type" (harmless). `mark` is User Timing Level 2,
// present on every engine that has PerformanceObserver at all, so a throw isolates the shape.
//
// 🪤 `window.PerformanceObserver`, matching the SDK's own `'PerformanceObserver' in window`
// guard — and reached through `window` so the absent-global case is a plain falsy read rather than
// a ReferenceError anywhere this module is evaluated outside a browser.
//
// The probe observes nothing (no `buffered`, disconnected immediately) and runs once per
// `initFaro`. What it deliberately does NOT catch: an engine that accepts the `type:` form but
// does not support the `navigation` entry type. That is a real class — MDN puts
// `PerformanceNavigationTiming` at Safari 15 while the `type:` form is Safari 14, so all of
// Safari 14.x sits in it — and gating on entry-type membership as well would still not recover
// anything, because the data does not exist on those engines. It would only add a skip event, and
// it would go stale the moment the SDK observes a different type.
//
// 🪤 IT IS NOT A CLEAN NO-OP THERE, THOUGH, and an earlier version of this paragraph said "no
// exception, and no data lost, because there was none to lose". The second half is wrong.
// `instrumentation.js` awaits `getNavigationTimings(pushEvent)`, which resolves only from inside
// the navigation observer's callback, and only then reaches `observeResourceTimings`. With no
// navigation entry the promise never settles, so RESOURCE timings are lost too — and `resource`
// goes back to Safari 11 — along with a permanently-pending promise and a retained
// `readystatechange` listener. The decision not to gate on membership stands; the reason given
// for it was too flattering.
const PROBE_ENTRY_TYPE = 'mark';

function engineAcceptsPerformanceObserverType() {
  let probe = null;
  try {
    // 🪤 THE READ IS INSIDE THE TRY, and it was outside until review. `window.PerformanceObserver`
    // is a property access on a global anything in this realm can redefine — a browser extension's
    // content script, a customer script injected into the widget document — and an accessor that
    // THROWS was the one shape of thirteen probed that escaped this function. It does not merely
    // skip the instrumentation: the throw reaches `initFaro`'s terminal catch, which sets
    // `faro = null`, so the whole RUM instance is lost for that embed. Strictly worse than the
    // `return false` the condition deserves, and the leg below asserting the probe cannot escape
    // `initFaro` stayed green through it, because containment at the boundary is not the same as
    // returning a boolean.
    const Observer = typeof window === 'undefined' ? undefined : window.PerformanceObserver;
    if (!Observer) return false;
    probe = new Observer(() => {});
    probe.observe({ type: PROBE_ENTRY_TYPE });
  } catch (rejected) {
    return false;
  } finally {
    if (probe) {
      try {
        probe.disconnect();
      } catch (undisconnectable) {
        // Nothing to undo: the probe observes `mark` and no code reads those entries.
      }
    }
  }
  return true;
}

// 🔴🔴 ONE DETECT PER CAPABILITY, AND AN EARLIER VERSION GATED BOTH ON `.at`.
//
// That version's argument was: the SDK's performance instrumentation never calls `.at`, but it
// calls `observer.observe({ type: … })` — which is exactly the
// `PerformanceObserverInit.entryTypes is required` error — so since "every engine that rejects
// `type:` predates `.at` by years", `.at` is a SUPERSET condition and one detect covers both.
// It labelled that half an inference rather than a measurement. The inference is real; the
// CONCLUSION drawn from it is not, and it is wrong in the direction that silently deletes data.
//
// 🪤 SUPERSET IS THE PROBLEM, NOT THE JUSTIFICATION. The premise is TRUE — an engine that
// rejects `type:` really does predate `.at` — but "one detect covers both" does not follow from
// it. If {rejects `type:`} ⊂ {lacks `.at`}, then gating on `.at` also drops every engine in the
// GAP — {supports `type:`, lacks `.at`} — and on those engines the performance instrumentation
// works perfectly. The gap is years wide: `.at` is Chrome 92 / Safari 15.4 (2021-22), while the
// single-`type:` form is years older. So
// the old form removed real navigation and resource timings from a population that was throwing
// NOTHING, while the claim "dropping it removes an exception and no data" was true only for the
// subset. VERIFIED against the installed SDK: `instrumentation-performance` guards solely with
// `'PerformanceObserver' in window` (`performanceUtils.js` `performanceObserverSupported`) and
// then calls `observer.observe({ type: …, buffered: true })` with no `entryTypes` fallback in
// both `navigation.js` and `resource.js`.
//
// 🔴🔴 THE SENTENCE THAT USED TO FINISH THAT PARAGRAPH WAS FALSE, UNDER A "VERIFIED" STAMP.
// It read: "and neither the SDK nor `web-vitals` contains `.at` anywhere in the installed tree,
// so `.at` was only ever a proxy for someone else's age." The SDK half is true — zero `.at` in
// `dist/esm` and `dist/cjs`. The `web-vitals` half is false, and it contradicts this same block
// twice: forty lines up it says web-vitals "calls `.at(-1)` on an array literal", and
// `ENGINE_LIMITED_INSTRUMENTATIONS` below names `Array.prototype.at` as the MEASURED thrower.
// MEASURED 2026-08-25, `grep -rn '\.at(' node_modules/web-vitals/dist/modules/` -> FIVE hits:
// `lib/LayoutShiftManager`, `lib/InteractionManager`, `attribution/onFCP`, `attribution/onLCP`,
// `attribution/onINP`. And that is the tree the widget bundles — the SDK's
// `webVitalsWithAttribution` imports from `web-vitals/attribution`.
//
// The claim the paragraph was reaching for, and which IS true: the SDK's PERFORMANCE
// instrumentation contains no `.at`, which is why gating THAT on `.at` was only ever a proxy for
// someone else's age. `web-vitals` contains five, which is why `engineRunsWebVitals` exists.
// A false statement that one grep settles is worse under a VERIFIED banner than without one: it
// is the sentence a maintainer would cite to delete the detect.
//
// So each instrumentation is gated on the capability IT needs.
//
// 🟢 THE GAP THIS BLOCK USED TO DESCRIBE IS CLOSED. It is kept because the SHAPE of the
// mistake is the useful part, and because a reader who finds the old detect in git history should
// find the reason it went.
//
// The old detect asked whether `PerformanceObserver.supportedEntryTypes` existed, on the stated
// premise that it "was added to the spec alongside the single-`type:` form". It was not: WebKit
// shipped the property 2019-01-25 and did not accept `type:` until 2020-07-28, eighteen months
// apart — Safari 13 versus Safari 14. So Safari 13.x had the property, passed the detect, kept
// the instrumentation and threw the exact error the detect existed to prevent. MEASURED in Faro
// RUM on postv4, a 24h window to 2026-08-25: 17 of 76 `entryTypes` exceptions, and that cohort
// reported ZERO `.at` errors so nothing else was dropping it either.
//
// `engineAcceptsPerformanceObserverType` now PROBES — see its own block above. The probe covers
// both real cohorts, because what Safari 12.1.x and Safari 13.x share is the throw, not the
// property. The spec models that shape directly (`withEntryTypesRequired`), where it used to
// model the cohort by DELETING `supportedEntryTypes` — an engine that never existed, which is how
// the detect could be wrong with the suite green.
//
// 🪤 THE LESSON, WHICH IS THE PART WORTH KEEPING: the whole argument for the proxy rested on
// "these two capabilities shipped together", and nobody checked. Two capabilities that feel
// contemporaneous is not a measurement, and a version claim is as checkable as any other number
// in this file. The one that replaced it was checked — commit dates, in the vendor's own history,
// quoted above.
//
// 🪤 AND "DROPPING IT REMOVES AN EXCEPTION AND NO DATA" IS FALSE FOR THE WEB-VITALS ENTRY.
// True for the performance instrumentation: `observe({type:'navigation'})` throws before any
// `pushEvent`. Not true for web-vitals. `webVitalsWithAttribution`'s `initialize()` calls
// measureCLS / measureFCP / measureINP / measureLCP / measureTTFB, and the TTFB path is `.at`-free
// end to end — MEASURED 2026-08-25: zero `.at(` in `attribution/onTTFB.js` or `onTTFB.js`, and
// none of the six lib modules they import is among the five `.at` sites. All five sites sit in
// ASYNC callbacks, so `measureTTFB()` has already registered before any of them can throw. On a
// `.at`-less engine the SDK therefore DOES emit a real measurement carrying `ttfb` and its
// dns/connection/request/waiting/cache durations, and skipping the instrumentation deletes that
// series.
//
// 🔴 CONFIRMED IN THE FIELD, not just read off the source. Faro RUM, postv4, a 24h window to
// 2026-08-25, Chrome 109.0.5414.117 / Android 13 — the largest `.at`-throwing cohort (186 of 339,
// with Mobile Safari 16.3 the next 133):
//
//     `kind=measurement` from that build      3,749
//       …carrying `ttfb`                        117
//       …carrying `lcp` (a `.at`-path metric)     0
//
// Exactly the predicted shape: the TTFB half gets through, the `.at` half never does. So the
// trade is real and now costed — FCP/LCP/INP/CLS exceptions removed, ~117 TTFB measurements a
// day lost — and the skip event is what makes that cost countable instead of silent. It is not
// the free win the old sentence claimed.
//
// 🪤 A HARDCODED VENDOR NAME IS A FILTER WITH A HALF-LIFE — a rename upstream turns
// it into a no-op that nothing can see. `test/unit-vue/faro-performance-feature-detect.spec.js`
// asserts both names against the REAL installed SDK for that reason. Filtering by
// name is the SDK's own idiom: its `makeCoreConfig` drops the navigation
// instrumentation the same way.
const ENGINE_LIMITED_INSTRUMENTATIONS = [
  {
    name: `${WEB_SDK_INSTRUMENTATION_PREFIX}web-vitals`,
    // The MEASURED thrower of both `.at` errors.
    capability: 'Array.prototype.at',
    runnable: engineRunsWebVitals,
  },
  {
    name: `${WEB_SDK_INSTRUMENTATION_PREFIX}performance`,
    // The capability the `entryTypes is required` error is actually about — the WebIDL member
    // whose absence produces it, mirroring the error's own vocabulary so the event and the
    // exception it replaces are greppable together.
    // 🪤 THIS SAID `PerformanceObserver.supportedEntryTypes`, which was the old PROXY and not the
    // capability. It rode into RUM on every skip event, so a reader chasing the degraded cohort
    // was pointed at a property Safari 13.x demonstrably HAS.
    capability: 'PerformanceObserverInit.type',
    runnable: engineAcceptsPerformanceObserverType,
  },
];

// The event name that records a skip. See selectRunnableInstrumentations.
const SKIPPED_INSTRUMENTATION_EVENT = 'widget_faro_instrumentation_skipped';

// Drop the instrumentations this engine cannot run, keep everything else.
//
// 🔑 AND EMIT ONE EVENT WHEN IT SKIPS — a judgement call, argued because the
// alternative (staying silent) is defensible too. Silent would make an absent
// web-vital indistinguishable from a client that simply had none to report, which is
// the same blindness `NOT_ALLOWED_MEASUREMENT_TYPE` exists to avoid a few hundred
// lines up: this codebase's standing answer to "filter it out" is "filter it out and
// COUNT it". Concretely it buys three things — the size of the degraded population
// becomes measurable instead of inferred, the fix becomes verifiable (the event
// appears as the THREE widget-attributable classes disappear — not four; `Load failed`
// is the disjoint cohort and stays), and it is strictly LESS traffic than
// today, since one event replaces the two-to-three exceptions each affected client
// currently sends.
//
// It rides `pushTelemetryEvent`, not the instance this function is about to create:
// that helper never throws and buffers until Faro is ready — both call sites run
// `flushTelemetry()` immediately after `initFaro`.
//
// 🪤 THE STATION AND host_origin STAMPS ARE NOT UNIVERSAL, and this comment used to say they
// were ("already stamps station and host_origin", flatly). True on app.vue's path, which calls
// `setTelemetryStation` and `setTelemetryHost` before `initFaro`. NOT true on main.js's degraded
// mount path, which calls `initFaro` and `flushTelemetry` and neither setter — so a skip event
// from that path carries no station, and only whatever `initFaro` itself puts in
// `user.attributes`. That path is the one that runs when the app failed to mount, i.e. exactly
// when a reader would most want to know which station they are looking at. Not fixed here
// because setting it belongs at the call site, not in this function.
//
// On an embed where init fails the event simply never flushes, which is the correct
// outcome and needs no code.
//
// `indexOf`, not `includes`: this function runs ON the old engines it is written for, so it uses
// nothing newer than they have.
// 🪤 THAT REASON IS INERT, and it is kept only because the STYLE is right. `Array.prototype.includes`
// is Chrome 47 / Safari 9 (2015-16), older than every engine in either documented cohort — the
// `.at` cohort needs Chrome 92 / Safari 15.4 and the `entryTypes` cohort is Safari 13-14 — so
// `includes` would have run fine on all of them. The rule "use nothing newer than the engines this
// protects" is still the right rule; this particular application of it never bought anything, and
// saying so stops the next reader citing it as evidence that the floor was measured.
function selectRunnableInstrumentations(instrumentations) {
  // 🪤 THIS GUARD AND THE `.name` FALLBACK BELOW ARE UNPINNED, AND THAT IS THE DECISION, not an
  // oversight. Review ran twelve independent mutations against this function; eleven died, and
  // the one survivor was deleting `|| []` — the spec stays fully green because
  // `getWebInstrumentations()` always returns an array, so no reachable call passes undefined.
  // A leg that pins it would test the mock, not the SDK: it would assert that a value this
  // function never receives is handled, and it would go on passing if the real contract
  // changed. Recorded here instead, which is what the finding itself recommended — the point of
  // a defensive line is that nothing exercises it.
  const offered = instrumentations || [];
  const kept = [];
  const skipped = [];
  const missing = [];
  for (let i = 0; i < offered.length; i += 1) {
    const instrumentation = offered[i];
    const instrumentationName = (instrumentation && instrumentation.name) || '';
    let limit = null;
    for (let j = 0; j < ENGINE_LIMITED_INSTRUMENTATIONS.length; j += 1) {
      if (ENGINE_LIMITED_INSTRUMENTATIONS[j].name === instrumentationName) {
        limit = ENGINE_LIMITED_INSTRUMENTATIONS[j];
        break;
      }
    }
    // 🔑 PER ENTRY. An engine that lacks `.at` but accepts `observe({type:})` keeps its
    // navigation and resource timings — that gap population is why this is not one detect.
    if (limit === null || limit.runnable()) {
      kept.push(instrumentation);
    } else {
      skipped.push(instrumentationName.replace(WEB_SDK_INSTRUMENTATION_PREFIX, ''));
      if (missing.indexOf(limit.capability) === -1) missing.push(limit.capability);
    }
  }
  if (skipped.length > 0) {
    pushTelemetryEvent(SKIPPED_INSTRUMENTATION_EVENT, {
      // Name what was actually dropped, not the constant — on an SDK where only one
      // of the two is present, this has to say which.
      // 🪤 NOT DEDUPED, while `capability` below IS. The two read as parallel comma lists and are
      // not built the same way: a duplicate entry in `offered` would appear twice here and once
      // there. Unreachable against the real SDK, which offers each instrumentation once — left
      // asymmetric rather than "fixed" behind a guard nothing can exercise, and written down so
      // the asymmetry is a decision instead of a discrepancy the next reader has to re-derive.
      instrumentations: skipped.join(','),
      // …and WHICH capability was missing, now that the two can be missing independently.
      // An event naming `Array.prototype.at` for a `PerformanceObserverInit.type` failure would
      // send the next reader back to the wrong cohort. (This said "a `supportedEntryTypes`
      // failure" until the detect stopped being about that property — nothing emits that string.)
      capability: missing.join(','),
    });
  }
  return kept;
}

// Initialize Grafana Faro frontend RUM for the widget (page-loads, JS errors,
// web-vitals, browser spans). Callers (F-5/MR-70) only invoke this when a
// collector URL exists AND the visitor isn't a synthetic agent — so a
// bot/no-collector embed never reaches this module's network path at all.
//
// The collector is same-origin per cluster (Varnish proxies /-/faro/collect
// to the node-local Alloy faro.receiver for the IN-APP player; the embedded
// widget's own origin differs — see F-6's CORS follow-up), so this mirrors
// postv2's initFaro shape with the widget's own app name + station stamping.
export function initFaro(url, {
  version, station, environment, hostOrigin,
} = {}) {
  // MR-72 (F-7): station used to ride ONLY the tracing spans (stampStation
  // below) — the fetch/xhr instrumentation's derived Faro events, meaning
  // ~20 of ~22 RUM signals per pageview (web-vitals, resource timing,
  // navigation, session_start/resume) carried no station at all.
  //
  // ⚠️ First attempt put station in `app` meta — WRONG, empirically refuted
  // against the real avow collector: the wire payload DID carry
  // `meta.app.station` (captured the raw POST body), but Alloy's
  // `faro.receiver` decodes `app` (like `sdk`/`browser`/`os`/`page`/
  // `session`'s non-attributes fields) against a fixed known-fields schema
  // and silently drops anything else — a curl probe posting a bespoke
  // top-level `meta.station` was dropped too. Only the SDK's genuinely
  // free-form `attributes` bags (on `user`/`session`/`page`) survive, proven
  // by 3 curl probes each landing in Loki as `<category>_attr_<key>`.
  // `session.attributes` was ruled out too: the installed SDK's session
  // instrumentation + both sessionManager strategies call
  // `faro.api.setSession()` on their own for rotation/persistence, which
  // REPLACES the whole `session` meta object — an attribute stashed there
  // would be silently wiped on the next rotation. `user` meta has no such
  // internal auto-caller anywhere in the SDK (only `setSession` appears in
  // any default instrumentation), so `user.attributes` is the one slot
  // nothing else will ever silently clobber. Lands in Loki as
  // `user_attr_station`, not `app_station`. Omitted entirely (no `user` key
  // at all) when neither value is given, mirroring stampStation's own
  // `if (station)` guard.
  //
  // 🪤 AND THEN !263 REPEATED THE BUG THIS BLOCK IS THE POST-MORTEM FOR.
  // `host_origin` shipped through `core/telemetry.js`'s `withEmbedContext`, which
  // decorates `pushEvent` and nothing else — so it rode 4 hand-pushed events while
  // the exception stream, measurements, logs and every web-vital carried none, and
  // the commit subject said "on every Faro event". That is the identical ~20-of-22
  // shape, with the remedy written out immediately above it. Measured against the
  // installed SDK: pushEvent → station ✓ host_origin ✓; pushError / pushMeasurement
  // / pushLog → station ✓ host_origin ✗. It rides `user.attributes` now, so the
  // claim is true and buffered-before-init events no longer matter for this field.
  // 🔴 FAIL CLOSED WITHOUT A COLLECTOR. An origin nobody has wired up should produce NO
  // telemetry rather than a console full of CORS errors. Both call sites already test
  // `faroUrl` first; asserting it here means a future one cannot forget to.
  if (!url) return null;

  // 🔑 NORMALIZE ONCE, HERE, so both stamps THIS FUNCTION owns inherit it —
  // user.attributes AND the tracing spans. Normalising at the two separately is how one of
  // them gets missed: the station rode both in the customer's own embed-code casing, so
  // MEASURED on postv4 over 13h, `KTICAM` (6,195 events) and `kticam` (1,957) were two
  // labels for one station in every per-station aggregate, and neither was the real number.
  // api.js already lowercased the station for request paths, so this makes the RUM label
  // and the URL agree by construction rather than by coincidence. See src/utils/station.js.
  //
  // 🪤 "SO ANY CALLER INHERITS IT" WOULD BE FALSE, AND THAT PHRASING SHIPPED ONCE. There are
  // FIVE paths that put a station into telemetry, not the three an earlier telling counted,
  // and normalising here reaches only the two above:
  //
  //   1. `user.attributes.station` — here
  //   2. the tracing-span stamp     — here
  //   3. `core/telemetry.js`'s module-level stamp (`setTelemetryStation`) — normalises
  //      itself
  //   4. `reportNotAllowedIfApplicable`'s measurement `context` — a SEPARATE call site in
  //      this file, taking the raw value from twelve callers; it normalises itself now
  //      (review item S11), and did not before
  //   5. a caller that supplies its own `station` KEY to `pushTelemetryEvent` —
  //      `withEmbedContext` guards on hasOwnProperty and deliberately never overwrites it,
  //      so such a caller bypasses (3) entirely. `widget_layout` in app.vue is exactly
  //      that caller, and it shipped the raw-cased station: 49.7% of those events carried
  //      an uppercase label while `user_attr_station` on the same event was lowercase
  //      (review item M1).
  //
  // The rule that is actually true: EVERY SITE THAT COMPOSES A STATION LABEL NORMALISES AT
  // THAT SITE. There is no inheritance to rely on.
  const stationLabel = normalizeStation(station);

  const userAttributes = {};
  if (stationLabel) userAttributes.station = stationLabel;
  if (hostOrigin) userAttributes.host_origin = hostOrigin;
  const hasUserAttributes = Object.keys(userAttributes).length > 0;

  // 🔴 FARO BEING UNREACHABLE IS NOT A HARD FAIL. The widget runs on third-party pages, and
  // a collector that is down, ad-blocked (routine for RUM endpoints) or answering without
  // CORS must cost the customer nothing. The principle was already written down at main.js's
  // degraded path — "observability must never surface to the embed" — but it lived in the
  // CALLERS, so it held only where one remembered to wrap this. It belongs here, where every
  // call site inherits it.
  //
  // Returns null rather than rethrowing, so a caller can tell the difference between "no
  // telemetry" and "telemetry"; nothing is required to look.
  let faro = null;
  try {
    faro = initializeFaro({
      url,
      app: {
        name: 'webwidget',
        version: version || 'dev',
        environment,
      },
      user: hasUserAttributes ? { attributes: userAttributes } : undefined,
      ignoreErrors: IGNORE_ERRORS,
    // 🪤 THE OBSERVED FAILURE THREW NOTHING. The collector answered 202 and the browser
    // blocked READING the response for want of Access-Control-Allow-Origin, so the SDK's OWN
    // logger printed the wall of red — which no try/catch can reach. An embed shares its
    // console with the customer's site, so a telemetry problem that fills it becomes a
    // support ticket about our widget for something that broke nothing.
      internalLoggerLevel: InternalLoggerLevel.OFF,
    // See redactPageUrl. Applied on the way out because the SDK's page meta is
    // registered unconditionally and cannot be overridden from config. Mutating a
    // copy of `meta.page` rather than the item keeps this a projection, not a hook
    // with opinions — and it returns the item, never null, so this can never
    // become an accidental sampler.
    // NB Object.assign, not object spread — this repo's eslint parser is ES2017
    // and rejects `...obj` in an object literal (build.target is es2015).
      beforeSend: (item) => {
        // 🪤 `next` IS HOISTED OUT OF THE `try` ON PURPOSE. It used to be declared inside,
        // so the catch below could only fall back to the ORIGINAL `item` — and once this
        // hook grew a second step after the meta rebuild, a throw in that step would have
        // shipped the un-redacted url while a correct redaction sat in scope and unused.
        // Best-effort: the catch now returns whatever redaction succeeded, never worse
        // than its input. (`redactPageUrl` is total — 23 input classes, zero throws — so
        // this is defence against an exotic accessor, not a live path.)
        let next = item;
        try {
          const pageUrl = next && next.meta && next.meta.page && next.meta.page.url;
          // 🪤 NOT an early `return item` any more (D-214). It used to bail here, which
          // meant an item with no `meta.page.url` skipped every later redaction too — and
          // the navigation event below is reached on exactly those items in a stubbed
          // harness. Fall THROUGH instead of returning.
          if (pageUrl) {
            const redactedUrl = redactPageUrl(pageUrl);
            const page = Object.assign({}, next.meta.page, { url: redactedUrl });
            const meta = Object.assign({}, next.meta, { page });
            next = Object.assign({}, next, { meta });
            // D-221 — the same string can also ride `stacktrace.frames[].filename`.
            // Fed the RAW url (what the SDK substituted) and the REDACTED one (what the
            // page meta now says) so the two fields cannot disagree.
            next = redactStackFrameFilenames(next, pageUrl, redactedUrl);
          }
          return redactEventUrlAttribute(next);
        } catch (e) {
          return next; // telemetry must never surface to the embed
        }
      },
      instrumentations: [
      // captureConsole:false — never ship console.warn/error payloads to Loki
      // from a page we don't control (arbitrary third-party host page).
      // Filtered, not disabled: see selectRunnableInstrumentations. Tracing stays
      // OUTSIDE the filter deliberately — it is not affected and spans must keep
      // reporting on a degraded engine.
        ...selectRunnableInstrumentations(getWebInstrumentations({ captureConsole: false })),
        new TracingInstrumentation({
          instrumentationOptions: {
            fetchInstrumentationOptions: {
              applyCustomAttributesOnSpan: fetchCustomAttributeFunctionWithDefaults((span) =>
              stampStation(span, stationLabel)
            ),
            },
            xhrInstrumentationOptions: {
              applyCustomAttributesOnSpan: (span) => stampStation(span, stationLabel),
            },
          },
        }),
      ],
    });
  } catch (e) {
    // Swallowed on purpose and NOT logged: the SDK's own logger is already silenced above
    // for exactly this reason, and re-printing here would reintroduce the noise into the
    // embedding page. A null return is the whole signal.
    faro = null;
  }

  return faro;
}
